import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { FileText, Download, Loader2, Eye, ShieldCheck, CheckCheck } from "lucide-react";
import { toast } from "sonner";

const NATIVE_PREVIEW = new Set(["pdf", "png", "jpg", "jpeg", "webp", "gif", "txt"]);
const OFFICE = new Set(["doc", "docx", "xls", "xlsx", "ppt", "pptx", "odt"]);

function Watermark({ text }) {
  return (
    <div aria-hidden className="absolute inset-0 pointer-events-none overflow-hidden z-20 select-none">
      <div className="absolute inset-0 flex flex-wrap gap-12 p-10 opacity-20" style={{ transform: "rotate(-30deg) scale(1.4)", transformOrigin: "center" }}>
        {Array.from({ length: 40 }).map((_, i) => (
          <div key={i} className="text-slate-600 font-display font-bold text-sm tracking-wider whitespace-nowrap">
            {text}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function JobPreviewDialog({ jobId, open, onOpenChange, onChanged }) {
  const [job, setJob] = useState(null);
  const [activeFile, setActiveFile] = useState(null);
  const [blobUrl, setBlobUrl] = useState(null);
  const [textContent, setTextContent] = useState("");
  const [loading, setLoading] = useState(false);
  const [approving, setApproving] = useState(false);

  const loadJob = () => {
    if (!jobId) return;
    api.get(`/store/jobs/${jobId}`).then((r) => {
      setJob(r.data);
      if (r.data.files?.length) setActiveFile((prev) => prev || r.data.files[0]);
    }).catch(() => toast.error("Failed to load job"));
  };

  useEffect(() => {
    if (!open || !jobId) return;
    setJob(null); setActiveFile(null); setBlobUrl(null); setTextContent("");
    loadJob();
  }, [open, jobId]);

  useEffect(() => {
    if (!activeFile) return;
    let objUrl;
    setLoading(true); setBlobUrl(null); setTextContent("");
    api.get(`/store/files/${activeFile.id}/preview`, { responseType: "blob" })
      .then(async (r) => {
        const ext = (activeFile.ext || "").toLowerCase();
        if (ext === "txt") {
          const txt = await r.data.text();
          setTextContent(txt);
        } else {
          objUrl = URL.createObjectURL(r.data);
          setBlobUrl(objUrl);
        }
      })
      .catch((err) => {
        const status = err?.response?.status;
        if (status === 503) toast.error("Office preview unavailable — LibreOffice not installed on server");
        else if (status === 504) toast.error("Conversion timed out");
        else toast.error(`Could not load ${activeFile.filename}`);
      })
      .finally(() => setLoading(false));
    return () => { if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [activeFile]);

  const downloadActive = async () => {
    if (!activeFile) return;
    const r = await api.get(`/store/files/${activeFile.id}`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = activeFile.filename; a.click();
    URL.revokeObjectURL(url);
  };

  const approve = async () => {
    setApproving(true);
    try {
      await api.post(`/store/jobs/${jobId}/approve`);
      toast.success("Job approved — sending to printer");
      loadJob();
      onChanged?.();
    } catch (e) { toast.error("Could not approve"); } finally { setApproving(false); }
  };

  const ext = (activeFile?.ext || "").toLowerCase();
  const isOffice = OFFICE.has(ext);
  const isNative = NATIVE_PREVIEW.has(ext);
  const isImage = ["png", "jpg", "jpeg", "webp", "gif"].includes(ext);
  const isPdfLike = ext === "pdf" || isOffice; // office is converted to PDF server-side
  const isTxt = ext === "txt";
  const canPreview = isNative || isOffice;

  const watermarkText = useMemo(() => job ? `${job.job_number} · ${job.store_name} · CONFIDENTIAL` : "Print Plus", [job]);
  const isPending = job?.status === "pending_approval";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl h-[85vh] p-0 gap-0 flex flex-col">
        <DialogHeader className="p-4 border-b border-slate-200 shrink-0">
          <DialogTitle className="font-display flex items-center gap-3">
            <Eye className="w-4 h-4 text-orange-500" />
            {job ? job.job_number : "Loading…"}
            {job && <span className="text-xs font-normal text-slate-400 font-mono">· {job.total_pages} pages · ₹{job.amount}</span>}
            {isPending && <Badge className="bg-amber-500 ml-2">Awaiting approval</Badge>}
          </DialogTitle>
        </DialogHeader>
        {isPending && (
          <div className="p-3 bg-amber-50 border-b border-amber-200 flex items-center justify-between gap-3" data-testid="approval-banner">
            <div className="flex items-center gap-2 text-sm text-amber-900">
              <ShieldCheck className="w-4 h-4" />
              <span>Review the documents below, then approve to release to the printer.</span>
            </div>
            <Button size="sm" disabled={approving} onClick={approve} className="bg-emerald-500 hover:bg-emerald-600" data-testid="approve-job-btn">
              <CheckCheck className="w-4 h-4 mr-1" />{approving ? "Approving…" : "Approve & print"}
            </Button>
          </div>
        )}
        <div className="flex-1 flex min-h-0">
          <aside className="w-56 border-r border-slate-200 overflow-auto shrink-0" data-testid="job-preview-files">
            {(job?.files || []).map((f, i) => {
              const cfg = (job.configs || [])[i] || {};
              return (
                <button key={f.id} onClick={() => setActiveFile(f)}
                  className={`w-full text-left p-3 border-b border-slate-100 hover:bg-slate-50 flex gap-2 ${activeFile?.id === f.id ? "bg-orange-50" : ""}`}
                  data-testid={`preview-file-${f.filename}`}>
                  <FileText className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{f.filename}</div>
                    <div className="text-xs text-slate-400 font-mono">{f.pages}p × {cfg.copies || 1} · {cfg.color_mode || "bw"}{cfg.duplex ? " · duplex" : ""} · {cfg.orientation || "auto"}</div>
                  </div>
                </button>
              );
            })}
          </aside>
          <main className="flex-1 min-w-0 bg-slate-100 flex flex-col relative">
            <div className="p-2 border-b border-slate-200 bg-white flex items-center justify-between shrink-0 z-30">
              <div className="text-xs text-slate-500 font-mono truncate px-2">{activeFile?.filename}</div>
              <Button size="sm" variant="outline" onClick={downloadActive} disabled={!activeFile} data-testid="preview-download-btn">
                <Download className="w-3.5 h-3.5 mr-1.5" />Download
              </Button>
            </div>
            <div className="flex-1 min-h-0 grid place-items-center overflow-auto relative">
              {loading && <Loader2 className="w-6 h-6 animate-spin text-slate-400" />}
              {!loading && !canPreview && activeFile && (
                <div className="text-center p-8">
                  <FileText className="w-10 h-10 mx-auto text-slate-300 mb-3" />
                  <p className="text-sm text-slate-500">Inline preview isn't available for <span className="font-mono">.{ext}</span> files.</p>
                  <Button size="sm" onClick={downloadActive} className="mt-3 bg-orange-500 hover:bg-orange-600"><Download className="w-3.5 h-3.5 mr-1.5" />Download to view</Button>
                </div>
              )}
              {!loading && isImage && blobUrl && <img src={blobUrl} alt={activeFile.filename} className="max-w-full max-h-full object-contain" />}
              {!loading && isPdfLike && blobUrl && <iframe src={blobUrl} title={activeFile.filename} className="w-full h-full bg-white" data-testid="pdf-preview" />}
              {!loading && isTxt && <pre className="w-full h-full p-4 bg-white text-xs font-mono whitespace-pre-wrap overflow-auto">{textContent}</pre>}
              {!loading && canPreview && (blobUrl || textContent) && <Watermark text={watermarkText} />}
            </div>
          </main>
        </div>
      </DialogContent>
    </Dialog>
  );
}
